// SPDX-FileCopyrightText: 2026 Igor Brkic <igor@hyperglitch.com>
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * Tabs: the bridges that hold a panel together, and the cuts that free it.
 *
 * Ported from `_generate_tab` (`sigmaKi.py:523-611`), which is itself a
 * version-independent rewrite of KiKit's `Substrate.tab()` with two fixes the
 * comment there explains: it considers interior rings as well as exteriors, so
 * a frame's inner edge is visible to a ray; and it always bites the globally
 * nearest face, so a generous `max_length` cannot weld a strip straight across
 * an intervening board.
 *
 * A tab is a ray. It starts at `origin`, which must lie in the gap rather than
 * inside the substrate, and extrudes a strip `width` wide along `direction`
 * until it reaches the nearest face. `sides: both` extrudes the other way too,
 * bridging the gap with a break-off cut on each face.
 *
 * **On not needing polygon booleans.** The Python works against `merged`, a
 * shapely union of every piece. Nothing here unions anything: a tab origin is
 * required to be outside the substrate and the ray stops at the first face it
 * meets, so a seam between two overlapping rails is never reachable, and
 * "inside the union" is the same question as "inside any piece". That is what
 * keeps this project free of a polygon-clipping dependency, which would have
 * been its largest and least trustworthy component.
 */

import type { Point } from '../model/geometry.ts';
import { type Polygon, type Ring, pointInPolygon, ringsOf } from './outline.ts';
import { SpecError, type DefaultsSpec, type TabEntry } from './spec.ts';

/** KiKit's `SHP_EPSILON`, in millimetres. */
const EPSILON = 1e-3;

export interface TabGeometry {
  polys: Polygon[];
  cuts: Point[][];
  error: string | null;
}

// --- vector helpers -----------------------------------------------------------------

function normalize(v: Point): Point {
  const len = Math.hypot(v[0], v[1]);
  if (len === 0) throw new SpecError('direction must not be [0, 0]');
  // KiKit rounds the normalised direction; matching it keeps grazing cases identical.
  return [Math.round((v[0] / len) * 1e4) / 1e4, Math.round((v[1] / len) * 1e4) / 1e4];
}

/** KiKit's `makePerpendicular`: (x, y) → (y, −x). */
function perpendicular(v: Point): Point {
  return [v[1], -v[0]];
}

function dot(a: Point, b: Point): number {
  return a[0] * b[0] + a[1] * b[1];
}

// --- ray casting --------------------------------------------------------------------

/** Where along a ring a point sits: edge index plus fraction along that edge. */
interface RingPosition {
  point: Point;
  edge: number;
  t: number;
  /** Distance from the ray origin, for choosing the closest hit. */
  distance: number;
}

/**
 * The closest point at which a ray meets a ring, within `maxDistance`.
 *
 * Mirrors KiKit's `closestIntersectionPoint`, which intersects a finite segment
 * with the ring and keeps whichever crossing lies nearest the origin.
 */
function closestIntersection(
  origin: Point,
  direction: Point,
  ring: Ring,
  maxDistance: number,
): RingPosition | null {
  let best: RingPosition | null = null;

  for (let i = 0; i < ring.length; i++) {
    const a = ring[i]!;
    const b = ring[(i + 1) % ring.length]!;
    const ex = b[0] - a[0];
    const ey = b[1] - a[1];

    const denominator = direction[0] * ey - direction[1] * ex;
    if (Math.abs(denominator) < 1e-12) continue; // parallel

    const ox = a[0] - origin[0];
    const oy = a[1] - origin[1];
    // Solving `origin + s·direction = a + t·edge` by Cramer's rule: `s` is how
    // far along the ray, `t` how far along the edge.
    const s = (ox * ey - oy * ex) / denominator;
    const t = (ox * direction[1] - oy * direction[0]) / denominator;

    if (s < 0 || s > maxDistance) continue;
    if (t < 0 || t > 1) continue;

    if (best === null || s < best.distance) {
      best = {
        point: [origin[0] + direction[0] * s, origin[1] + direction[1] * s],
        edge: i,
        t,
        distance: s,
      };
    }
  }

  return best;
}

/** Length of a polyline. */
function polylineLength(points: Point[]): number {
  let total = 0;
  for (let i = 1; i < points.length; i++) {
    total += Math.hypot(points[i]![0] - points[i - 1]![0], points[i]![1] - points[i - 1]![1]);
  }
  return total;
}

/**
 * The stretch of `ring` running forward from `from` to `to`.
 *
 * KiKit's `biteBoundary`. Walking forward from one position to the other gives
 * one of the two arcs between them; asking for the reverse gives the other, and
 * the caller keeps whichever makes the better face.
 */
function arcBetween(ring: Ring, from: RingPosition, to: RingPosition): Point[] {
  const out: Point[] = [from.point];
  const n = ring.length;

  // Same edge, and `to` lies ahead of `from` on it: a straight run, no vertices.
  if (from.edge === to.edge && to.t >= from.t) {
    out.push(to.point);
    return out;
  }

  let edge = from.edge;
  for (let guard = 0; guard <= n; guard++) {
    out.push(ring[(edge + 1) % n]!);
    edge = (edge + 1) % n;
    if (edge === to.edge) {
      out.push(to.point);
      return out;
    }
  }
  return out;
}

function polygonArea(ring: Ring): number {
  let sum = 0;
  for (let i = 0; i < ring.length; i++) {
    const a = ring[i]!;
    const b = ring[(i + 1) % ring.length]!;
    sum += a[0] * b[1] - b[0] * a[1];
  }
  return Math.abs(sum / 2);
}

/** True when the point lies inside any piece of the substrate. */
export function insideSubstrate(pieces: Polygon[], x: number, y: number): boolean {
  return pieces.some((p) => pointInPolygon(p, x, y));
}

/**
 * Whether a tab origin sits strictly inside the substrate.
 *
 * Ported from `_tab_origin_inside` (`sigmaKi.py:614-619`). A point exactly on an
 * edge is legal — that is the normal way to aim a tab at a board face — so the
 * check excludes anything within a hair of the boundary.
 */
export function tabOriginInside(pieces: Polygon[], x: number, y: number): boolean {
  if (!insideSubstrate(pieces, x, y)) return false;
  for (const piece of pieces) {
    for (const ring of ringsOf(piece)) {
      for (let i = 0; i < ring.length; i++) {
        if (distanceToSegment([x, y], ring[i]!, ring[(i + 1) % ring.length]!) <= EPSILON) {
          return false;
        }
      }
    }
  }
  return true;
}

function distanceToSegment(p: Point, a: Point, b: Point): number {
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  const lenSq = dx * dx + dy * dy;
  if (lenSq === 0) return Math.hypot(p[0] - a[0], p[1] - a[1]);
  const t = Math.max(0, Math.min(1, ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / lenSq));
  return Math.hypot(p[0] - (a[0] + t * dx), p[1] - (a[1] + t * dy));
}

export interface Tab {
  poly: Polygon;
  /** The face the tab meets, oriented with material on its left. */
  cut: Point[];
}

/**
 * One half of a tab: the strip from `origin` to the nearest face along `direction`.
 *
 * Returns null when the ray never lands with its full width on a face.
 */
export function generateTab(
  pieces: Polygon[],
  origin: Point,
  direction: Point,
  width: number,
  maxLength: number,
): Tab | null {
  const d = normalize(direction);
  const perp = perpendicular(d);
  const sideA: Point = [origin[0] + (perp[0] * width) / 2, origin[1] + (perp[1] * width) / 2];
  const sideB: Point = [origin[0] - (perp[0] * width) / 2, origin[1] - (perp[1] * width) / 2];

  let best: { distance: number; ring: Ring; a: RingPosition; b: RingPosition } | null = null;

  for (const piece of pieces) {
    for (const ring of ringsOf(piece)) {
      const a = closestIntersection(sideA, d, ring, maxLength);
      const b = closestIntersection(sideB, d, ring, maxLength);
      if (a === null || b === null) continue;

      const da = dot([a.point[0] - sideA[0], a.point[1] - sideA[1]], d);
      const db = dot([b.point[0] - sideB[0], b.point[1] - sideB[1]], d);
      // Both edges of the strip must actually travel to reach the face; a zero
      // means the ray is grazing the ring the origin already sits on.
      if (Math.min(da, db) < EPSILON) continue;

      const distance = Math.max(da, db);
      if (best === null || distance < best.distance) best = { distance, ring, a, b };
    }
  }

  if (best === null) return null;

  // The tab body has to bite INTO the target, not merely touch it. KiKit says why
  // in tab()'s partition-line branch: "there is a numerical instability on small
  // slopes that yields artifacts on substrate union". A straight run between two
  // rounded corners gets its endpoints from arc tessellation, so it is never
  // exactly axis-aligned -- a face laid exactly on such an edge leaves slivers
  // that the union then renders as outline segments running past the corner where
  // they should stop. The cut stays on the true edge; only the body overshoots.
  const penetrating = (face: Point[]): Point[] =>
    face.map(([x, y]) => [x + d[0] * EPSILON, y + d[1] * EPSILON] as Point);

  // The ring between the two landing points can be walked either way. Build both
  // and keep the shorter face, which is the one facing the tab.
  const candidates: Array<{ face: Point[]; poly: Polygon }> = [];

  const forward = arcBetween(best.ring, best.b, best.a);
  candidates.push({
    face: forward,
    poly: { ext: [...penetrating(forward), sideA, sideB], holes: [] },
  });

  const backward = arcBetween(best.ring, best.a, best.b);
  candidates.push({
    face: backward,
    poly: { ext: [...penetrating(backward), sideB, sideA], holes: [] },
  });

  const valid = candidates.filter((c) => c.poly.ext.length >= 3 && polygonArea(c.poly.ext) > 0);
  if (valid.length === 0) return null;

  let chosen = valid[0]!;
  let shortest = polylineLength(chosen.face);
  for (const candidate of valid.slice(1)) {
    const length = polylineLength(candidate.face);
    if (length < shortest) {
      shortest = length;
      chosen = candidate;
    }
  }

  return { poly: chosen.poly, cut: orientCut(pieces, chosen.face) };
}

/**
 * Point the cut so that board material lies to its left.
 *
 * KiKit offsets mouse bites to the left of the cut, so a cut running the wrong
 * way puts the drill holes in the air instead of in the board. The tab body is
 * not part of the substrate yet, so exactly one side of the face is material and
 * probing settles it.
 */
function orientCut(pieces: Polygon[], face: Point[]): Point[] {
  if (face.length < 2) return face;

  const midIndex = Math.max(1, Math.floor(face.length / 2));
  const a = face[midIndex - 1]!;
  const b = face[midIndex]!;
  const mx = (a[0] + b[0]) / 2;
  const my = (a[1] + b[1]) / 2;

  const tx = b[0] - a[0];
  const ty = b[1] - a[1];
  const norm = Math.hypot(tx, ty);
  if (norm === 0) return face;

  const probe = 3 * EPSILON;
  const left: Point = [mx - (ty / norm) * probe, my + (tx / norm) * probe];
  const right: Point = [mx + (ty / norm) * probe, my - (tx / norm) * probe];

  const leftIsMaterial = insideSubstrate(pieces, left[0], left[1]);
  const rightIsMaterial = insideSubstrate(pieces, right[0], right[1]);
  return !leftIsMaterial && rightIsMaterial ? [...face].reverse() : face;
}

/**
 * Geometry for every `tabs[]` entry.
 *
 * Errors attach to the tab that caused them rather than throwing, so one
 * misaimed tab does not blank the canvas — the same contract as the engine's
 * `preview_tabs`.
 */
export function previewTabs(
  pieces: Polygon[],
  tabs: TabEntry[],
  defaults: DefaultsSpec = {},
): TabGeometry[] {
  const defaultWidth = defaults.tab_width ?? 3;
  const defaultMaxLength = defaults.tab_max_length ?? 50;

  return tabs.map((tab, index) => {
    const entry: TabGeometry = { polys: [], cuts: [], error: null };

    try {
      const origin = tab.origin;
      const direction = tab.direction;
      if (!Array.isArray(origin) || !Array.isArray(direction) ||
          typeof origin[0] !== 'number' || typeof origin[1] !== 'number' ||
          typeof direction[0] !== 'number' || typeof direction[1] !== 'number') {
        throw new SpecError("needs 'origin: [x, y]' and 'direction: [dx, dy]'");
      }

      const width = tab.width ?? defaultWidth;
      const maxLength = tab.max_length ?? defaultMaxLength;
      const sides = tab.sides ?? 'both';
      if (sides !== 'both' && sides !== 'one') {
        throw new SpecError("sides must be 'both' or 'one'");
      }

      if (tabOriginInside(pieces, origin[0], origin[1])) {
        throw new SpecError(
          'origin lies inside the panel substrate; tab origins must be in the gap ' +
            'between pieces (on an edge or outside)',
        );
      }

      const directions: Point[] =
        sides === 'one'
          ? [[direction[0], direction[1]]]
          : [[direction[0], direction[1]], [-direction[0], -direction[1]]];

      for (const d of directions) {
        const result = generateTab(pieces, [origin[0], origin[1]], d, width, maxLength);
        if (result === null) {
          throw new SpecError(
            'the ray does not land with its whole width on any board/rail face ' +
              `within max_length=${maxLength} mm`,
          );
        }
        entry.polys.push(result.poly);
        if (result.cut.length >= 2) entry.cuts.push(result.cut);
      }
    } catch (e) {
      entry.error = e instanceof Error ? e.message : String(e);
      entry.polys = [];
      entry.cuts = [];
    }

    return entry;
  });
}

/** Index of the tab entry, for reporting. */
export function describeTab(index: number, message: string): string {
  return `tabs[${index}]: ${message}`;
}
